import { useEffect, useState } from "react";
import { motion, AnimatePresence, useReducedMotion } from "framer-motion";
import { X } from "lucide-react";
import TopoCanvas from "./TopoCanvas";
import ReconQuiz from "./ReconQuiz";
import ReconBooking from "./ReconBooking";

const EASE = [0.16, 1, 0.3, 1];

function ReconFlowContent({ onClose, step, setStep, quizAnswers, setQuizAnswers, padding }) {
  return (
    <div className={`relative z-10 flex h-full flex-col overflow-y-auto ${padding}`}>
      <div className="flex items-start justify-between">
        <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-rust">
          CH.00 // Recon
        </p>
        <button
          onClick={onClose}
          data-testid="recon-flow-close"
          aria-label="Close"
          className="-mr-1 -mt-1 flex h-8 w-8 items-center justify-center rounded-full text-ink-dim transition-colors hover:text-ink"
        >
          <X className="h-4 w-4" />
        </button>
      </div>

      <div className="mt-3">
        {step === "quiz" ? (
          <ReconQuiz
            onComplete={(answers) => {
              setQuizAnswers(answers);
              setStep("booking");
            }}
          />
        ) : (
          <ReconBooking quizAnswers={quizAnswers} onBack={() => setStep("quiz")} />
        )}
      </div>
    </div>
  );
}

/**
 * variant="modal": the original centered, backdrop-blocking presentation —
 *   this is the site's one manual entry point (Nav's "Start a build") and
 *   its behavior here is untouched.
 * variant="drawer": a non-blocking panel that slides in from the right
 *   edge of the viewport, used only for the 45s auto-surface. No backdrop —
 *   the rest of the page stays interactive.
 */
export default function ReconFlow({ open, onClose, variant = "modal" }) {
  const reduce = useReducedMotion();
  const [step, setStep] = useState("quiz");
  const [quizAnswers, setQuizAnswers] = useState(null);

  useEffect(() => {
    if (open) {
      setStep("quiz");
      setQuizAnswers(null);
    }
  }, [open]);

  useEffect(() => {
    if (!open) return;
    const onKey = (e) => {
      if (e.key === "Escape") onClose();
    };
    document.addEventListener("keydown", onKey);
    if (variant === "modal") {
      const prevOverflow = document.body.style.overflow;
      document.body.style.overflow = "hidden";
      return () => {
        document.removeEventListener("keydown", onKey);
        document.body.style.overflow = prevOverflow;
      };
    }
    return () => document.removeEventListener("keydown", onKey);
  }, [open, onClose, variant]);

  if (variant === "drawer") {
    return (
      <AnimatePresence>
        {open && (
          <motion.div
            data-testid="recon-flow-drawer"
            role="dialog"
            aria-modal="false"
            aria-label="Recon quiz"
            className="fixed inset-y-0 right-0 z-[100] flex w-full max-w-xs flex-col overflow-hidden border-l border-hairline bg-paper shadow-2xl md:inset-y-8 md:right-4 md:max-h-[560px] md:rounded-lg md:border"
            initial={reduce ? { opacity: 0 } : { x: "100%" }}
            animate={reduce ? { opacity: 1 } : { x: 0 }}
            exit={reduce ? { opacity: 0 } : { x: "100%" }}
            transition={{ duration: reduce ? 0.2 : 0.35, ease: EASE }}
          >
            <TopoCanvas variant="light" />
            <ReconFlowContent
              onClose={onClose}
              step={step}
              setStep={setStep}
              quizAnswers={quizAnswers}
              setQuizAnswers={setQuizAnswers}
              padding="p-5"
            />
          </motion.div>
        )}
      </AnimatePresence>
    );
  }

  return (
    <AnimatePresence>
      {open && (
        <motion.div
          data-testid="recon-flow-overlay"
          className="fixed inset-0 z-[100] flex items-center justify-center bg-ink/40 px-5 py-10 backdrop-blur-sm"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.25 }}
          onClick={(e) => {
            if (e.target === e.currentTarget) onClose();
          }}
        >
          <motion.div
            data-testid="recon-flow-panel"
            role="dialog"
            aria-modal="true"
            aria-label="Recon quiz and booking"
            className="relative max-h-[85vh] w-full max-w-lg overflow-hidden rounded-lg border border-hairline bg-paper shadow-2xl"
            initial={{ opacity: 0, scale: 0.96, y: 12 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.96, y: 12 }}
            transition={{ duration: 0.3, ease: EASE }}
          >
            <TopoCanvas variant="light" />
            <ReconFlowContent
              onClose={onClose}
              step={step}
              setStep={setStep}
              quizAnswers={quizAnswers}
              setQuizAnswers={setQuizAnswers}
              padding="p-6 md:p-8"
            />
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
